import {
  Database,
  ShieldCheck,
  FileSearch,
  FileSignature,
  Banknote,
  OctagonAlert,
  ArrowRight,
} from 'lucide-react';
import type { ComponentType } from 'react';

export interface TrustControl {
  control: string;
  purpose: string;
}

export interface TrustSection {
  id: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
  summary: string;
  controls: TrustControl[];
  keyStatement: string;
  risksReduced: string[];
}

export const TRUST_INTRO =
  'REDEX is a governed capital execution environment. This page summarizes the controls that protect tenant data, transaction state, legal records, and operational continuity.';

export const TRUST_SECTIONS: TrustSection[] = [
  {
    id: 'backups',
    title: 'Backups & Recoverability',
    icon: Database,
    summary:
      'Tenant and platform data are recoverable, versioned, and stored off-server. Backups are not just created — they are verified restorable.',
    controls: [
      { control: 'Automated PostgreSQL backups', purpose: 'Preserves recoverability for all tenant databases' },
      { control: 'Restore verification', purpose: 'Proves backups are usable, not just present' },
      { control: 'KMS-encrypted S3 storage', purpose: 'Protects backup artifacts at rest with AWS-managed keys' },
      { control: 'Versioned retention tiers', purpose: 'Daily, weekly, and monthly snapshots protect historical states' },
      { control: 'Public access blocked', purpose: 'Backup bucket is inaccessible outside authorized IAM roles' },
    ],
    keyStatement:
      'Backup creation, encryption, and restore verification run automatically without operator intervention.',
    risksReduced: [
      'Tenant data loss',
      'Transaction evidence loss',
      'Operational irrecoverability',
      'Key-person recovery dependence',
    ],
  },
  {
    id: 'isolation',
    title: 'Tenant Isolation',
    icon: ShieldCheck,
    summary:
      'Tenants share execution infrastructure while maintaining isolated data, policies, and transaction state. Cross-tenant access is not filtered — it is structurally impossible.',
    controls: [
      { control: 'Separate PostgreSQL databases', purpose: 'Each tenant operates against its own data store' },
      { control: 'Process-level service isolation', purpose: 'Tenants run as independent system services' },
      { control: 'Validated tenant headers', purpose: 'Unknown tenant identifiers are rejected before reaching routes' },
      { control: 'Tenant-scoped policy sets', purpose: 'Feature flags, branding, and rails are resolved per tenant' },
      { control: 'Isolated portal presentation', purpose: 'Each tenant has its own domain, branding, and navigation' },
    ],
    keyStatement:
      'Tenants operate on shared execution infrastructure while maintaining isolated data, policies, and transaction state.',
    risksReduced: [
      'Cross-tenant data leakage',
      'Policy contamination',
      'Operational confusion between tenants',
      'Multi-tenant trust collapse',
    ],
  },
  {
    id: 'audit',
    title: 'Audit Logging & Attribution',
    icon: FileSearch,
    summary:
      'Administrative and transactional actions are attributable and reconstructible. Every critical operation carries request, tenant, and actor context.',
    controls: [
      { control: 'Request correlation IDs', purpose: 'Every request carries a unique identifier through the full stack' },
      { control: 'Structured JSON logs', purpose: 'Production logs are machine-parseable with consistent schema' },
      { control: 'Admin audit log', purpose: 'Records action, actor, tenant, target, and before/after state' },
      { control: 'Request-aware tracing', purpose: 'Operational traces link to specific requests and users' },
    ],
    keyStatement:
      'Critical platform actions are logged with request, tenant, and actor context to preserve operational accountability.',
    risksReduced: [
      'Untraceable administrative actions',
      'Forensic blind spots',
      'Poor incident reconstruction',
      'Operator distrust during disputes',
    ],
  },
  {
    id: 'signatures',
    title: 'Signature Integrity & Legal Evidence',
    icon: FileSignature,
    summary:
      'Agreement acceptance is evidence-bearing execution, not UI theater. Signed documents are preserved as cryptographically verifiable records.',
    controls: [
      { control: 'SHA-256 bound signature records', purpose: 'Each acceptance is tied to a specific document hash' },
      { control: 'Immutable acceptance state', purpose: 'Signature records cannot be modified after execution' },
      { control: 'S3-hosted signed artifacts', purpose: 'Original signed documents are preserved externally' },
      { control: 'Engagement-linked compliance records', purpose: 'Signatures are tied to the transactions they govern' },
      { control: 'Version-aware tracking', purpose: 'Document version at time of signing is recorded permanently' },
    ],
    keyStatement:
      'Signed agreements are preserved as cryptographically verifiable execution records, not visual confirmations.',
    risksReduced: [
      'Contract ambiguity',
      'Weak dispute defense',
      'Unverifiable agreement state',
      'Non-defensible document execution',
    ],
  },
  {
    id: 'settlement',
    title: 'Settlement Controls',
    icon: Banknote,
    summary:
      'Capital does not move because a button was clicked. It moves because execution conditions passed internal policy gates and transaction state checks.',
    controls: [
      { control: 'Deterministic gating', purpose: 'Settlement requires passing policy and state checks before execution' },
      { control: 'Database transaction wrapping', purpose: 'Financial flows are atomic — they succeed completely or roll back' },
      { control: 'Idempotent transfer keys', purpose: 'Duplicate execution is prevented at the protocol level' },
      { control: 'Internal state independence', purpose: 'Transaction state is tracked independently of external provider status' },
      { control: 'Multi-rail settlement paths', purpose: 'ACH and crypto settle through governed execution paths' },
    ],
    keyStatement:
      'Capital movement is gated by internal policy and transaction state before settlement is allowed to execute.',
    risksReduced: [
      'Duplicate execution',
      'Settlement ambiguity',
      'External provider drift',
      'Irreconcilable payment state',
    ],
  },
  {
    id: 'freeze',
    title: 'Freeze & Escalation Controls',
    icon: OctagonAlert,
    summary:
      'The platform can halt activity intentionally when risk or policy requires it. Freeze controls are wired into financial routes, not layered on after the fact.',
    controls: [
      { control: 'Deal-level freeze', purpose: 'Individual deals can be frozen without affecting other operations' },
      { control: 'Tenant-wide emergency freeze', purpose: 'All financial operations halt immediately when activated' },
      { control: 'Route-level freeze guards', purpose: 'Investment and ACH routes check freeze state before executing' },
      { control: 'Escalation-aware model', purpose: 'Freeze actions are logged with actor, reason, and timestamp' },
    ],
    keyStatement:
      'Active control boundaries can halt deal or tenant activity when governance conditions require intervention.',
    risksReduced: [
      'Uncontrolled transaction continuation',
      'Delayed operational response',
      'Inability to contain active risk',
      'Manual-only intervention dependence',
    ],
  },
];

export const TRUST_FORWARD_STATEMENT =
  'The current trust model is active today. The next layer of platform maturity is focused on deeper reconciliation automation, provider abstraction, environment isolation, restore runbooks, and operational dashboards.';

export const FORWARD_ICON = ArrowRight;
